import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { referenceService } from '../../services/api';

const ContactManagement = ({ user }) => {
  const canManage = ['Administrator', 'Manager'].includes(user?.role);
  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Hide navbar when any modal is open
  useModal(showModal || showEditModal, () => {
    setShowModal(false);
    setShowEditModal(false);
  });

  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    OrganizationId: '',
    designation: '',
    department: '',
    email: '',
    phone: '',
    mobile: ''
  });

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const [contactsRes, orgsRes] = await Promise.all([
        referenceService.getContacts(),
        referenceService.getOrgs()
      ]);
      setContacts(contactsRes.data);
      setOrganizations(orgsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedContact) {
        await referenceService.updateContact(selectedContact.id, formData);
        Swal.fire('Success', 'Contact updated successfully!', 'success');
        setShowEditModal(false);
        setSelectedContact(null);
        loadContacts();
      } else {
        await referenceService.createContact(formData);
        Swal.fire('Success', 'Contact added successfully!', 'success');
        setShowModal(false);
        loadContacts();
      }
      setFormData({
        name: '',
        OrganizationId: '',
        designation: '',
        department: '',
        email: '',
        phone: '',
        mobile: ''
      });
    } catch (error) {
      console.error(error);
      if (selectedContact) setShowEditModal(false); else setShowModal(false); // Close on error
      Swal.fire('Error', 'Failed to save contact', 'error');
    }
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      OrganizationId: contact.OrganizationId || '',
      designation: contact.designation || '',
      department: contact.department || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = async (contactId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await referenceService.deleteContact(contactId);
        Swal.fire('Deleted!', 'Contact has been deleted.', 'success');
        loadContacts();
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Failed to delete contact', 'error');
      }
    }
  };

  const filteredContacts = contacts.filter(contact =>
    !searchTerm ||
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (contact.organization && contact.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (contact.designation && contact.designation.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Contact Management
          </h2>
          <p className="text-muted mb-0">Manage your contacts and relationships</p>
        </div>

        {canManage && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle"></i> Add Contact
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Contact Directory</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Organization</th>
                  <th>Designation</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center">Loading...</td></tr>
                ) : filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  filteredContacts.map((contact) => (
                    <tr key={contact.id}>
                      <td><strong>{contact.name}</strong></td>

                      <td>{contact.Organization ? contact.Organization.name : '-'}</td>
                      <td>{contact.designation || '-'}</td>
                      <td>{contact.email || '-'}</td>
                      <td>{contact.phone || '-'}</td>
                      <td>
                        {canManage && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(contact)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(contact.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {
        showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Contact</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Organization</label>
                        <select
                          className="form-select"
                          value={formData.OrganizationId}
                          onChange={(e) => setFormData({ ...formData, OrganizationId: e.target.value })}
                          required
                        >
                          <option value="">Select Organization</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Designation</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Phone</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Add Contact</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Modal (copy of Add Modal with prefilled data) */}
      {
        showEditModal && selectedContact && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Contact</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setSelectedContact(null); }}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {/* Same fields as Add */}
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Name *</label>
                        <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Organization</label>
                        <select className="form-select" value={formData.OrganizationId} onChange={(e) => setFormData({ ...formData, OrganizationId: e.target.value })} required>
                          <option value="">Select Organization</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Designation</label>
                        <input type="text" className="form-control" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} />
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Phone</label>
                        <input type="tel" className="form-control" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setSelectedContact(null); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Update Contact</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default ContactManagement;
